import { ProUserSettingsManager } from '@benzinga/pro-user-settings';
import { SessionContext } from '@benzinga/session-context';
import { isNilOrEmpty } from '@benzinga/utils';
import { DateTime } from 'luxon';
import React from 'react';

export const useOpraDeadline = () => {
  const OPRA_START_TIMESTAMP_KEY = 'opraModalMandatoryStartDate';
  const session = React.useContext(SessionContext);
  const proUserSettingsManager = session.getManager(ProUserSettingsManager);
  const getStoredMandatoryTimestamp = React.useCallback((): string | undefined => {
    const opraModalMandatoryStartDate = proUserSettingsManager.getSettings().opraModalMandatoryStartDate;
    if (isNilOrEmpty(opraModalMandatoryStartDate)) return undefined;
    return opraModalMandatoryStartDate;
  }, [proUserSettingsManager]);

  const getMandatoryAt = React.useCallback((): DateTime | undefined => {
    const storedISO = getStoredMandatoryTimestamp();
    if (isNilOrEmpty(storedISO)) return undefined;
    return DateTime.fromISO(storedISO, { zone: 'utc' }).plus({ days: 14 });
  }, [getStoredMandatoryTimestamp]);

  const isUserActionModalMandatory = React.useCallback(() => {
    const mandatoryAt = getMandatoryAt();
    if (!mandatoryAt) return false;
    return DateTime.now().toUTC() >= mandatoryAt;
  }, [getMandatoryAt]);

  const initMandatoryTimeStamp = React.useCallback(() => {
    const setSetting = proUserSettingsManager.setSetting;
    const storedOPRATimestamp = getStoredMandatoryTimestamp();
    if (!isNilOrEmpty(storedOPRATimestamp)) return;
    setSetting(OPRA_START_TIMESTAMP_KEY, DateTime.now().toUTC().toISO());
  }, [getStoredMandatoryTimestamp, proUserSettingsManager.setSetting]);

  const getModalCountdown = React.useCallback((): string => {
    if (isUserActionModalMandatory()) return 'Mailing Address is Required now';
    const mandatoryAt = getMandatoryAt();
    if (!mandatoryAt) return '';
    const now = DateTime.now().toUTC();
    if (now >= mandatoryAt) return 'Mailing Address is Required now';
    const daysRemaining = Math.ceil(mandatoryAt.startOf('day').diff(now, 'days').days);
    return daysRemaining === 0
      ? 'Mailing Address is Required now'
      : `Mailing Address will become mandatory after ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
  }, [getMandatoryAt, isUserActionModalMandatory]);

  return {
    getModalCountdown,
    initMandatoryTimeStamp,
    isUserActionModalMandatory,
  };
};
